import { API } from "@swivl-mono/api-helper";
import { filterClasses, resolveStepLibrary } from "@swivl-mono/helpers";
import type { ResolvedLibraryStep } from "@swivl-mono/helpers";
import type { CallScriptRequirement, CallScriptStepInterface, CallScriptStepSnapshot, CallScriptStepVersionInterface } from "@swivl-mono/types";
import {
  CALL_SCRIPT_REQUIREMENTS,
  CALL_SCRIPT_REQUIREMENT_KEYS,
  CALL_SCRIPT_STEP_AGENT_COPY_MAX_CHARS,
  CALL_SCRIPT_STEP_CRITERIA_MAX_CHARS,
  CALL_SCRIPT_STEP_GROUPS,
  CALL_SCRIPT_STEP_LABEL_MAX_CHARS,
  CALL_SCRIPT_STEP_SENTENCE_MAX_CHARS,
  callScriptStepGroupLabel
} from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ButtonVariant, Input, Panel, PanelPosition, Select, SelectSize, Switch, TextArea } from "@swivl-mono/ui";
import { useEffect, useMemo, useState } from "react";

import MembersModel from "../../../Models/Member/Members.Model";

/**
 * Steps tab (ENG-3402) — Swivl staff only. The org's step library: John's platform catalog with
 * the org's rows applied (org row wins per slug), grouped by his nine groups. Clicking a row opens
 * the same right-side panel the script editor uses: label, bot sentence, agent copy, completion
 * criteria, show to agents, must-haves. Add step creates an org step (slug from the label, `org-`
 * prefixed). Revert to default deletes an override; Delete removes an org step (refused while a
 * script uses it). Every save is LLM-checked on the server.
 */
interface CallScriptStepsSectionProps {
  orgId: number;
  onError: (message: string | null) => void;
}

function memberName(memberId: number | null): string {
  if (memberId == null) return "Swivl";
  const member = MembersModel.state.members?.find((m) => m.id === memberId);
  return member?.agentName || member?.account?.email || `Member #${memberId}`;
}

export function CallScriptStepsSection(props: CallScriptStepsSectionProps): React.ReactElement {
  const { orgId, onError } = props;
  const [rows, setRows] = useState<CallScriptStepInterface[]>([]);
  const [loading, setLoading] = useState(true);
  /** Slug being edited, "" for a new step, null for closed. */
  const [editingSlug, setEditingSlug] = useState<string | null>(null);

  const reload = async (): Promise<void> => {
    try {
      setRows(await API.CallScriptStep.listRows(orgId));
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const library = useMemo(() => resolveStepLibrary(rows), [rows]);
  const groups = useMemo(
    () => CALL_SCRIPT_STEP_GROUPS.map((g) => ({ ...g, steps: library.filter((s) => s.group === g.slug) })).filter((g) => g.steps.length > 0),
    [library]
  );
  const editing = editingSlug ? (library.find((s) => s.slug === editingSlug) ?? null) : null;
  const editingRow = editing?.rowId != null ? (rows.find((r) => r.id === editing.rowId) ?? null) : null;

  return (
    <section className="space-y-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="h-3 w-1 shrink-0 bg-grey-900" />
            <h2 className="text-base font-semibold text-grey-900">Step library</h2>
            <span className="rounded-full bg-grey-100 px-2 py-0.5 text-xs font-medium text-grey-700">Swivl staff</span>
          </div>
          <p className="pl-3 text-base text-grey-600">
            Every step a script can use, org-wide. Edit a platform step for this org, or add a step of your own. Changes apply to every script that
            uses the step.
          </p>
        </div>
        <Button color={ButtonColor.BLACK} disabled={loading} onClick={() => setEditingSlug("")}>
          Add step
        </Button>
      </div>

      {loading ? (
        <p className="pl-3 text-sm text-grey-500">Loading…</p>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <div key={group.slug} className="overflow-hidden rounded-lg border border-grey-200 bg-white">
              <p className="border-b border-grey-200 bg-grey-50 px-4 py-2 text-xs font-semibold tracking-wide text-grey-600 uppercase">
                {group.label}
              </p>
              <table className="w-full text-sm">
                <tbody>
                  {group.steps.map((step) => (
                    <tr
                      key={step.slug}
                      className="cursor-pointer border-b border-grey-100 last:border-b-0 hover:bg-grey-50"
                      onClick={() => setEditingSlug(step.slug)}
                    >
                      <td className="w-64 px-4 py-2 align-top">
                        <p className="font-medium text-grey-900">{step.label}</p>
                        <p className="text-xs text-grey-400">{step.slug}</p>
                      </td>
                      <td className="px-4 py-2 align-top text-grey-700">{step.sentence}</td>
                      <td className="w-56 px-4 py-2 align-top">
                        <div className="flex flex-wrap gap-1">
                          {step.source === "org" && step.platformDefault && <Badge>Customized here</Badge>}
                          {step.source === "org" && !step.platformDefault && <Badge>Org step</Badge>}
                          {step.showToAgents && <Badge muted>Shows to agents</Badge>}
                          {step.defaultRequirements.length > 0 && (
                            <Badge muted>Must-haves: {step.defaultRequirements.map((r) => CALL_SCRIPT_REQUIREMENTS[r].label).join(", ")}</Badge>
                          )}
                          {step.requiresPage && <Badge muted>Needs a page</Badge>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      <Panel
        isOpen={editingSlug !== null}
        onToggle={() => setEditingSlug(null)}
        position={PanelPosition.RIGHT}
        width="560px"
        title={editing ? `Edit ${editing.label}` : "New step"}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        shouldIgnoreOutsideClick={(e) =>
          e.target instanceof Element && !!e.target.closest("[data-radix-popper-content-wrapper],[data-floating-ui-portal]")
        }
      >
        {editingSlug !== null && (
          <StepEditor
            key={editingSlug}
            orgId={orgId}
            step={editing}
            row={editingRow}
            onSaved={async () => {
              await reload();
              setEditingSlug(null);
            }}
            onCancel={() => setEditingSlug(null)}
            onError={onError}
          />
        )}
      </Panel>
    </section>
  );
}

function Badge(props: { children: React.ReactNode; muted?: boolean }): React.ReactElement {
  return (
    <span
      className={filterClasses(
        "rounded-full px-2 py-0.5 text-xs font-medium",
        props.muted ? "bg-grey-100 text-grey-600" : "bg-orange-100 text-orange-700"
      )}
    >
      {props.children}
    </span>
  );
}

interface StepEditorProps {
  orgId: number;
  /** Null for a new org step. */
  step: ResolvedLibraryStep | null;
  /** The org row behind the step, when there is one. */
  row: CallScriptStepInterface | null;
  onSaved: () => Promise<void>;
  onCancel: () => void;
  onError: (message: string | null) => void;
}

type EditorTab = "edit" | "history";

function StepEditor(props: StepEditorProps): React.ReactElement {
  const { step, row } = props;
  const [group, setGroup] = useState<string>(step?.group ?? CALL_SCRIPT_STEP_GROUPS[0].slug);
  const [label, setLabel] = useState(step?.label ?? "");
  const [sentence, setSentence] = useState(step?.sentence ?? "");
  const [liveAgentCopy, setLiveAgentCopy] = useState(step?.liveAgentCopy ?? "");
  const [criteria, setCriteria] = useState(step?.coachingCompletionCriteria ?? "");
  const [showToAgents, setShowToAgents] = useState(step?.showToAgents ?? true);
  const [requirementsAllowed, setRequirementsAllowed] = useState(step?.requirementsAllowed === true);
  const [defaultRequirements, setDefaultRequirements] = useState<CallScriptRequirement[]>(step?.defaultRequirements ?? []);
  const [tab, setTab] = useState<EditorTab>("edit");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [versions, setVersions] = useState<CallScriptStepVersionInterface[] | null>(null);

  useEffect(() => {
    if (tab !== "history" || !row || versions) return;
    void API.CallScriptStep.listVersions(props.orgId, row.id)
      .then(setVersions)
      .catch((e) => props.onError(e instanceof Error ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, row?.id]);

  const canSave = !saving && label.trim().length > 0 && sentence.trim().length > 0;
  const isPlatform = !!step && !!(step.platformDefault || step.source === "platform");

  const snapshot = (): CallScriptStepSnapshot => ({
    group,
    label: label.trim(),
    sentence: sentence.trim(),
    liveAgentCopy: liveAgentCopy.trim(),
    coachingCompletionCriteria: criteria.trim(),
    showToAgents,
    requirementsAllowed,
    defaultRequirements: requirementsAllowed ? defaultRequirements : []
  });

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      await API.CallScriptStep.upsert(props.orgId, { slug: step?.slug, ...snapshot(), expectedRevision: row?.revision });
      await props.onSaved();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (): Promise<void> => {
    if (!row) return;
    const message = isPlatform
      ? `Revert '${row.label}' to the platform default for this org? Your changes stay in the history.`
      : `Delete '${row.label}'? Scripts that use it must drop it first.`;
    if (!confirm(message)) return;
    setSaveError(null);
    try {
      await API.CallScriptStep.remove(props.orgId, row.id);
      await props.onSaved();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleRestore = async (version: CallScriptStepVersionInterface): Promise<void> => {
    if (!row) return;
    if (!confirm(`Restore revision ${version.revision}? It is saved as a new revision; nothing is lost.`)) return;
    setSaveError(null);
    try {
      await API.CallScriptStep.restore(props.orgId, row.id, version.revision);
      await props.onSaved();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    }
  };

  const toggleRequirement = (r: CallScriptRequirement): void =>
    setDefaultRequirements((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex h-[50px] shrink-0 items-center gap-2 border-b border-grey-200 px-4">
        <p className="min-w-0 flex-1 truncate text-base font-semibold text-grey-900">{step ? `Edit ${step.label}` : "New step"}</p>
        {row && (
          <div className="flex rounded border border-grey-200 text-xs">
            {(["edit", "history"] as EditorTab[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={filterClasses("px-2 py-1", tab === t ? "bg-grey-900 text-white" : "text-grey-600 hover:bg-grey-50")}
              >
                {t === "edit" ? "Edit" : "History"}
              </button>
            ))}
          </div>
        )}
      </div>

      {tab === "history" && row ? (
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {!versions ? (
            <p className="text-sm text-grey-500">Loading…</p>
          ) : (
            <ul className="space-y-2">
              {versions.map((v) => (
                <li key={v.id} className="flex items-start justify-between gap-3 rounded border border-grey-200 p-3">
                  <div className="min-w-0 text-sm">
                    <p className="font-semibold text-grey-900">
                      Revision {v.revision} <span className="font-normal text-grey-500">· {v.note}</span>
                    </p>
                    <p className="text-grey-600">
                      {memberName(v.memberId)} · {v.createdAt ? new Date(v.createdAt).toLocaleString() : ""}
                    </p>
                    <p className="mt-1 truncate text-grey-500">{v.snapshot.sentence}</p>
                  </div>
                  {v.revision !== row.revision && (
                    <Button variant={ButtonVariant.GHOST} size={ButtonSize.SMALL} onClickAsync={() => handleRestore(v)}>
                      Restore
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {step?.platformDefault && (
            <p className="rounded border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
              This org overrides the platform step. Platform default: &ldquo;{step.platformDefault.sentence}&rdquo;
            </p>
          )}
          <Select
            label="Group"
            size={SelectSize.MEDIUM}
            hideClear
            selectedValues={[group]}
            options={CALL_SCRIPT_STEP_GROUPS.map((g) => ({ label: g.label, value: g.slug }))}
            onChange={(values) => {
              if (values[0]) setGroup(values[0]);
            }}
          />
          <Input label="Label" value={label} maxLength={CALL_SCRIPT_STEP_LABEL_MAX_CHARS} onChange={(e) => setLabel(e.target.value)} />
          <TextArea
            label="What the bot does"
            value={sentence}
            rows={2}
            maxLength={CALL_SCRIPT_STEP_SENTENCE_MAX_CHARS}
            placeholder="One instruction, imperative. Every fact comes from a tool."
            onChange={(e) => setSentence(e.target.value)}
          />
          <TextArea
            label="What the agent sees"
            value={liveAgentCopy}
            rows={2}
            maxLength={CALL_SCRIPT_STEP_AGENT_COPY_MAX_CHARS}
            placeholder="One or two plain lines for a live agent."
            onChange={(e) => setLiveAgentCopy(e.target.value)}
          />
          <TextArea
            label="Completion criteria"
            value={criteria}
            rows={2}
            maxLength={CALL_SCRIPT_STEP_CRITERIA_MAX_CHARS}
            placeholder='What the coaching engine checks to mark the step done, e.g. "The customer was asked what they plan to store."'
            onChange={(e) => setCriteria(e.target.value)}
          />
          <div className="flex items-center gap-3">
            <Switch on={showToAgents} activeColor="orange" onSet={(on) => setShowToAgents(on)} />
            <div>
              <p className="text-sm font-medium text-grey-900">Show to agents</p>
              <p className="text-xs text-grey-500">Appears in the Contact Center Script section once an agent has taken the conversation over.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch on={requirementsAllowed} activeColor="orange" onSet={(on) => setRequirementsAllowed(on)} />
            <div>
              <p className="text-sm font-medium text-grey-900">Can carry must-haves</p>
              <p className="text-xs text-grey-500">Only for steps that collect details from the customer.</p>
            </div>
          </div>
          {requirementsAllowed && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="mr-1 text-xs text-grey-500">Default must-haves:</span>
              {CALL_SCRIPT_REQUIREMENT_KEYS.map((r) => {
                const on = defaultRequirements.includes(r);
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => toggleRequirement(r)}
                    className={filterClasses(
                      "rounded-full border px-2 py-0.5 text-xs",
                      on ? "border-grey-900 bg-grey-900 text-white" : "border-grey-200 bg-white text-grey-600 hover:border-grey-400"
                    )}
                  >
                    {CALL_SCRIPT_REQUIREMENTS[r].label}
                  </button>
                );
              })}
            </div>
          )}
          {step && (
            <p className="text-xs text-grey-400">
              Slug {step.slug} · {callScriptStepGroupLabel(step.group)}
            </p>
          )}
        </div>
      )}

      {saveError && (
        <div className="shrink-0 border-t border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {saveError}
        </div>
      )}
      <div className="flex h-[50px] shrink-0 items-center justify-between gap-2 border-t border-grey-200 bg-white px-4">
        <div>
          {row && (
            <Button variant={ButtonVariant.GHOST} className="-ml-3" onClickAsync={handleRemove}>
              {isPlatform ? "Revert to default" : "Delete step"}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant={ButtonVariant.GHOST} onClick={props.onCancel}>
            Cancel
          </Button>
          <Button color={ButtonColor.BLACK} disabled={!canSave} onClickAsync={handleSave}>
            {saving ? "Checking & saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
